/**
 * OverviewTab — вкладка «Обзор» карточки: баннер закрытого доступа
 * (просрочка, конец триала, блокировка), Подписка с действиями команды
 * («Отметить оплату», «Закрыть доступ»), Основное. Клиника и соло —
 * разный набор строк.
 */

import type {ReactNode} from 'react';
import dayjs from 'dayjs';
import {CircleAlert, CircleCheck, Lock, Pencil, Unlock, UserX} from 'lucide-react';

import type {ClientDetail, ClientListItem, SubscriptionStatus} from '@services/clients';
import {Button} from '@components/ui/Button';
import {formatNumber} from '@utils/format';

import {renewalMeta} from '../../Clients.utils';

type OverviewTabProps = {
  client: ClientListItem;
  detail: ClientDetail;
  /** «Отметить оплату» — открыть доступ до даты */
  onActivate: () => void;
  /** «Закрыть доступ» решением Skrezo */
  onBlock: () => void;
  onEditMain: () => void;
  onChangeTariff: () => void;
};

/** Статусы, при которых клиника в режиме чтения — запись и оплаты закрыты */
const CLOSED: Partial<Record<SubscriptionStatus, {title: string; text: string}>> = {
  past_due: {
    title: 'Оплата не пришла — доступ закрыт',
    text: 'Клиника в режиме чтения: записи и оплаты закрыты, данные на месте. Когда деньги придут, отметьте оплату.',
  },
  trial_expired: {
    title: 'Пробный период закончился',
    text: 'Клиника в режиме чтения. Откройте доступ, когда придёт первая оплата.',
  },
  blocked: {
    title: 'Доступ закрыт командой Skrezo',
    text: 'Клиника в режиме чтения. Снимается отметкой оплаты.',
  },
};

const fmtDate = (iso: string | null): string =>
  iso ? dayjs(iso).format('D MMM YYYY').replace('.', '') : '—';

export const OverviewTab = ({
  client,
  detail,
  onActivate,
  onBlock,
  onEditMain,
  onChangeTariff,
}: OverviewTabProps) => {
  const isClinic = client.type === 'clinic';
  const status = client.subscriptionStatus;
  const closed = CLOSED[status];
  const overdue = status === 'past_due';
  const renew = renewalMeta(client.currentPeriodEnd);
  const renewText = overdue ? `${renew.text} · просрочено` : renew.text;
  /** Архивный клиент — действий с подпиской нет */
  const archived = status === 'cancelled';

  return (
    <div className="flex flex-col gap-3.5">
      {closed && <AccessBanner title={closed.title} text={closed.text} onActivate={onActivate} />}
      {detail.adminBlocked && (
        <div className="flex gap-[11px] rounded-[13px] border border-skr-line bg-card p-[13px_16px]">
          <UserX className="mt-px size-[18px] shrink-0 text-skr-danger" strokeWidth={1.9} />
          <div className="text-[12.5px] leading-[1.45] text-skr-ink2">
            <span className="font-semibold text-foreground">Аккаунт владельца заблокирован</span> —
            он не войдёт в кабинет. Снять блокировку можно в разделе «Аккаунты».
          </div>
        </div>
      )}

      {/* subscription */}
      <Card title="Подписка">
        {isClinic ? (
          <>
            <InfoRow label="Тариф" value="Клиника · ступенчатый" />
            <InfoRow
              label="Места"
              value={`${client.seatsUsed} / ${client.seatsLimit} занято`}
              mono
            />
            <InfoRow label="Дата продления" value={renewText} mono danger={overdue} />
            <CardRow label="Карта" value={<CardStatus attached={detail.cardAttached} />} />
            <InfoRow
              label="Выручка клиники"
              value={`${formatNumber(client.revenue)} сом`}
              mono
              last
            />
          </>
        ) : (
          <>
            <InfoRow label="Тариф" value="Соло · фикс. цена" />
            <InfoRow label="Цена" value="1 200 сом / мес" mono />
            <InfoRow label="Дата продления" value={renewText} mono danger={overdue} />
            <CardRow label="Карта" value={<CardStatus attached={detail.cardAttached} />} last />
          </>
        )}
        {!archived && (
          <div className="mt-3 flex gap-2">
            <Button className="h-[38px] flex-1 gap-1.5" onClick={onActivate}>
              <Unlock className="size-[15px]" strokeWidth={2} />
              Отметить оплату
            </Button>
            {status !== 'blocked' && (
              <Button
                variant="outline"
                className="h-[38px] flex-1 gap-1.5 text-destructive hover:text-destructive"
                onClick={onBlock}
              >
                <Lock className="size-[15px]" strokeWidth={2} />
                Закрыть доступ
              </Button>
            )}
          </div>
        )}
        {isClinic && (
          <Button variant="outline" className="mt-2 h-[38px] w-full" onClick={onChangeTariff}>
            Изменить тариф
          </Button>
        )}
      </Card>

      {/* solo activity */}
      {!isClinic && detail.activity && (
        <Card title="Активность">
          <div className="flex gap-[11px]">
            <Metric value={detail.activity.patients} label="пациентов" />
            <Metric value={detail.activity.visits} label="приёмов за месяц" />
          </div>
          <div className="mt-1.5 flex items-center justify-between pt-2.5 text-[13px]">
            <span className="text-muted-foreground">Последний вход</span>
            <span className="font-mono text-[13.5px]">{detail.activity.lastLogin}</span>
          </div>
        </Card>
      )}

      {/* main info */}
      <Card
        title="Основное"
        action={
          <Button
            variant="outline"
            size="sm"
            className="h-[30px] gap-1.5 px-[11px]"
            onClick={onEditMain}
          >
            <Pencil className="size-3.5" strokeWidth={1.9} />
            Изменить
          </Button>
        }
      >
        {isClinic ? (
          <>
            <InfoRow label="Адрес" value={detail.address ?? '—'} />
            <InfoRow label="Город" value={client.cityName} />
            <InfoRow label="Телефон бизнеса" value={detail.businessPhone ?? '—'} mono />
            <InfoRow label="Язык" value={detail.language} />
            <InfoRow label="Дата создания" value={fmtDate(client.createdAt)} mono last />
          </>
        ) : (
          <>
            <InfoRow label="Специализация" value={detail.specialization ?? '—'} />
            <InfoRow label="Город" value={client.cityName} />
            <InfoRow label="Адрес приёма" value={detail.address ?? '—'} />
            <InfoRow label="Язык" value={detail.language} />
            <InfoRow label="Дата регистрации" value={fmtDate(client.createdAt)} mono last />
          </>
        )}
      </Card>
    </div>
  );
};

/* ── локальные части ───────────────────────────────────────────── */

const Card = ({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <div className="rounded-[13px] border border-skr-line bg-card px-[18px] py-4">
    <div className="mb-3 flex items-center justify-between">
      <div className="text-sm font-semibold">{title}</div>
      {action}
    </div>
    {children}
  </div>
);

const InfoRow = ({
  label,
  value,
  mono,
  danger,
  last,
}: {
  label: string;
  value: string;
  mono?: boolean;
  danger?: boolean;
  last?: boolean;
}) => (
  <CardRow
    label={label}
    last={last}
    value={
      <span
        className={danger ? 'text-skr-danger' : undefined}
        style={mono ? {fontFamily: 'IBM Plex Mono, monospace'} : undefined}
      >
        {value}
      </span>
    }
  />
);

const CardRow = ({label, value, last}: {label: string; value: ReactNode; last?: boolean}) => (
  <div
    className={`flex items-center justify-between py-[9px] ${last ? '' : 'border-b border-skr-hairline'}`}
  >
    <span className="text-[13px] text-muted-foreground">{label}</span>
    <span className="text-right text-[13.5px]">{value}</span>
  </div>
);

const CardStatus = ({attached}: {attached: boolean}) =>
  attached ? (
    <span className="inline-flex items-center gap-1.5 text-[13.5px] text-skr-paid">
      <CircleCheck className="size-[15px]" strokeWidth={2.2} />
      Привязана
    </span>
  ) : (
    <span className="text-skr-faint">—</span>
  );

const Metric = ({value, label}: {value: number; label: string}) => (
  <div className="flex-1 rounded-[11px] border border-skr-line2 bg-[#F7F8FA] p-[12px_13px]">
    <div className="font-mono text-[22px] font-semibold">{formatNumber(value)}</div>
    <div className="mt-0.5 text-xs text-muted-foreground">{label}</div>
  </div>
);

const AccessBanner = ({
  title,
  text,
  onActivate,
}: {
  title: string;
  text: string;
  onActivate: () => void;
}) => (
  <div className="rounded-[13px] border border-[#F3D3CE] bg-[#FCEEEC] p-[15px_16px]">
    <div className="flex gap-[11px]">
      <CircleAlert className="mt-px size-5 shrink-0 text-skr-danger" strokeWidth={1.9} />
      <div className="flex-1">
        <div className="text-sm font-semibold text-[#A02E26]">{title}</div>
        <div className="mt-[3px] text-[12.5px] leading-[1.45] text-[#B4534A]">{text}</div>
        <button
          onClick={onActivate}
          className="mt-3 inline-flex h-[38px] items-center gap-[7px] rounded-[9px] bg-primary px-4 text-[13.5px] font-semibold text-primary-foreground shadow-[0_2px_6px_rgba(44,92,219,.3)] hover:bg-primary/90"
        >
          <Unlock className="size-4" strokeWidth={1.9} />
          Отметить оплату
        </button>
      </div>
    </div>
  </div>
);
